// src/lib/kinema/core/tools/SelectTool.ts
import type { Tool, EditorPointerEvent, HitResult, HitTestProvider, Point, Rect, TransformResult, DragMoveResult } from './types';
import type { GestureOverrideManager } from './GestureOverride';
import type { Editor } from '../Editor';
import { SELECT_LAYER, UPDATE_PROPERTY } from '../commands/commands';
import { calculateSnap } from './snapCalculation';
import { svgToPathPoints, insertPointAtIndex, removePointAtIndex } from './pathUtils';
import type { PathPoint } from './pathUtils';

export class SelectTool implements Tool {
  name = 'select';
  private mode: 'idle' | 'dragging' | 'marquee' = 'idle';
  private dragStartPoint: Point | null = null;
  private marqueeRect: Rect | null = null;
  private lastClickTime = 0;
  private lastClickLayerId: string | null = null;

  // Path editing sub-mode
  private editingPathLayerId: string | null = null;
  private editingPathPoints: PathPoint[] | null = null;
  private editingPathClosed = false;
  private selectedPointIndex: number | null = null;

  constructor(
    private editor: Editor,
    private hitTest: HitTestProvider,
    private gestures: GestureOverrideManager,
  ) {}

  onPointerDown(event: EditorPointerEvent, hit: HitResult): void {
    const now = Date.now();
    const isDoubleClick = now - this.lastClickTime < 300 && hit.layerId === this.lastClickLayerId;
    this.lastClickTime = now;
    this.lastClickLayerId = hit.layerId;

    // If in path edit mode and clicking outside the edited layer → exit edit mode
    if (this.editingPathLayerId && hit.layerId !== this.editingPathLayerId) {
      this.exitPathEditMode();
      // Fall through to normal select behavior
    }

    // Double-click on a path layer → enter path edit mode
    if (isDoubleClick && hit.layerId) {
      const layer = this.editor.getState().layers[hit.layerId];
      if (layer?.type === 'shape' && layer.shape === 'path') {
        this.enterPathEditMode(hit.layerId);
        return;
      }
    }

    // If in path edit mode, don't do normal selection
    if (this.editingPathLayerId) {
      return;
    }

    // Normal select behavior
    if (hit.layerId) {
      const isSelected = this.editor.getState().selectedIds.has(hit.layerId);

      if (event.shiftKey || event.metaKey) {
        this.editor.dispatchCommand(SELECT_LAYER, {
          layerId: hit.layerId, addToSelection: true,
        });
      } else if (!isSelected) {
        this.editor.dispatchCommand(SELECT_LAYER, { layerId: hit.layerId });
      }

      this.mode = 'dragging';
      this.dragStartPoint = event.point;
    } else {
      this.editor.dispatchCommand(SELECT_LAYER, { layerId: null });
      this.mode = 'marquee';
      this.dragStartPoint = event.point;
      this.marqueeRect = { x: event.point.x, y: event.point.y, width: 0, height: 0 };
    }
  }

  onPointerMove(event: EditorPointerEvent): void {
    if (this.mode === 'marquee' && this.dragStartPoint) {
      this.marqueeRect = {
        x: Math.min(this.dragStartPoint.x, event.point.x),
        y: Math.min(this.dragStartPoint.y, event.point.y),
        width: Math.abs(event.point.x - this.dragStartPoint.x),
        height: Math.abs(event.point.y - this.dragStartPoint.y),
      };
    }
  }

  onPointerUp(_event: EditorPointerEvent): void {
    if (this.mode === 'marquee' && this.marqueeRect) {
      const layerIds = this.hitTest.getLayersInRect(this.marqueeRect);
      if (layerIds.length > 0) {
        this.editor.batch(() => {
          for (const id of layerIds) {
            this.editor.dispatchCommand(SELECT_LAYER, { layerId: id, addToSelection: true });
          }
        });
      }
      this.marqueeRect = null;
    }
    this.mode = 'idle';
    this.dragStartPoint = null;
  }

  // ─── Path edit mode ─────────────────────────────────────

  enterPathEditMode(layerId: string): void {
    const layer = this.editor.getState().layers[layerId];
    if (!layer) return;

    // Get path points — from pathPoints property or parse from legacy pathData
    let points: PathPoint[] | null = layer.pathPoints as PathPoint[] | null;
    let closed = layer.pathClosed ?? false;

    if (!points && layer.pathData) {
      const parsed = svgToPathPoints(layer.pathData);
      if (parsed) {
        points = parsed.points;
        closed = parsed.closed;
      }
    }

    if (!points || points.length === 0) return;

    this.editingPathLayerId = layerId;
    this.editingPathPoints = points;
    this.editingPathClosed = closed;
    this.selectedPointIndex = null;
  }

  exitPathEditMode(): void {
    this.editingPathLayerId = null;
    this.editingPathPoints = null;
    this.editingPathClosed = false;
    this.selectedPointIndex = null;
  }

  /** Called by PathEditOverlay when a point is dragged */
  handlePointDrag(index: number, newX: number, newY: number): void {
    if (!this.editingPathPoints || !this.editingPathLayerId) return;

    const updated = this.editingPathPoints.map((p, i) =>
      i === index ? { ...p, x: newX, y: newY } : p,
    );
    this.commitPathPoints(updated);
  }

  /** Called by PathEditOverlay when a handle is dragged */
  handleHandleDrag(index: number, handleType: 'in' | 'out', dx: number, dy: number, altKey?: boolean): void {
    if (!this.editingPathPoints || !this.editingPathLayerId) return;

    const updated = this.editingPathPoints.map((p, i) => {
      if (i !== index) return p;
      const newPoint = { ...p };
      if (handleType === 'out') {
        newPoint.handleOut = { x: dx, y: dy };
        // Mirror the opposite handle (smooth) unless Alt is held (corner)
        if (!altKey) {
          newPoint.handleIn = { x: -dx, y: -dy };
        }
      } else {
        newPoint.handleIn = { x: dx, y: dy };
        if (!altKey) {
          newPoint.handleOut = { x: -dx, y: -dy };
        }
      }
      return newPoint;
    });
    this.commitPathPoints(updated);
  }

  /** Called by PathEditOverlay when a segment is clicked (add point) */
  handleSegmentClick(afterIndex: number): void {
    if (!this.editingPathPoints || !this.editingPathLayerId) return;
    const updated = insertPointAtIndex(this.editingPathPoints, afterIndex);
    this.commitPathPoints(updated);
    this.selectedPointIndex = afterIndex + 1;
  }

  /** Called by PathEditOverlay when a point is clicked (select it) */
  handlePointClick(index: number): void {
    this.selectedPointIndex = index;
  }

  /** Delete the selected point */
  deleteSelectedPoint(): void {
    if (!this.editingPathPoints || !this.editingPathLayerId || this.selectedPointIndex === null) return;
    if (this.editingPathPoints.length <= 2) return; // need at least 2 points

    const updated = removePointAtIndex(this.editingPathPoints, this.selectedPointIndex);
    this.commitPathPoints(updated);
    this.selectedPointIndex = null;
  }

  private commitPathPoints(points: PathPoint[]): void {
    if (!this.editingPathLayerId) return;

    this.editingPathPoints = points;
    this.editor.dispatchCommand(UPDATE_PROPERTY, {
      layerId: this.editingPathLayerId!,
      property: 'pathPoints',
      value: points,
    });
  }

  // ─── Accessors for the Compositor/Adapter ───────────────

  isEditingPath(): boolean {
    return this.editingPathLayerId !== null;
  }

  getEditingPathLayerId(): string | null {
    return this.editingPathLayerId;
  }

  getEditingPathPoints(): PathPoint[] | null {
    return this.editingPathPoints;
  }

  isEditingPathClosed(): boolean {
    return this.editingPathClosed;
  }

  getSelectedPointIndex(): number | null {
    return this.selectedPointIndex;
  }

  // ─── Snap + drag/transform (unchanged) ──────────────────

  private snapThreshold = 10;

  onDragStart(layerIds: string[]): void {
    for (const id of layerIds) {
      this.gestures.begin(id);
    }
  }

  onDragMove(layerId: string, currentPosition: Point, otherBounds: Rect[]): DragMoveResult | null {
    const layer = this.editor.getState().layers[layerId];
    if (!layer) return null;

    const movingBounds: Rect = {
      x: currentPosition.x - layer.anchorX,
      y: currentPosition.y - layer.anchorY,
      width: layer.width * layer.scaleX,
      height: layer.height * layer.scaleY,
    };

    const result = calculateSnap(movingBounds, otherBounds, this.snapThreshold);

    if (result.snappedX === undefined && result.snappedY === undefined) {
      return null;
    }

    return {
      snappedPosition: {
        x: result.snappedX !== undefined ? result.snappedX + layer.anchorX : currentPosition.x,
        y: result.snappedY !== undefined ? result.snappedY + layer.anchorY : currentPosition.y,
      },
      guides: result.guides,
    };
  }

  onDragEnd(layerIds: string[], finalPositions: Map<string, Point>): void {
    for (const id of layerIds) {
      this.gestures.end(id);
    }
    this.editor.batch(() => {
      for (const [id, pos] of finalPositions) {
        this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'x', value: pos.x });
        this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'y', value: pos.y });
      }
    });
  }

  onTransformStart(layerIds: string[]): void {
    for (const id of layerIds) {
      this.gestures.begin(id);
    }
  }

  onTransformEnd(layerIds: string[], finalBounds: Map<string, TransformResult>): void {
    for (const id of layerIds) {
      this.gestures.end(id);
    }
    this.editor.batch(() => {
      for (const [id, bounds] of finalBounds) {
        this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'x', value: bounds.x });
        this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'y', value: bounds.y });
        this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'scaleX', value: bounds.scaleX });
        this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'scaleY', value: bounds.scaleY });
        this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'rotation', value: bounds.rotation });
        if (bounds.width !== undefined) {
          this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'width', value: bounds.width });
        }
        if (bounds.height !== undefined) {
          this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'height', value: bounds.height });
        }
        if (bounds.anchorX !== undefined) {
          this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'anchorX', value: bounds.anchorX });
        }
        if (bounds.anchorY !== undefined) {
          this.editor.dispatchCommand(UPDATE_PROPERTY, { layerId: id, property: 'anchorY', value: bounds.anchorY });
        }
      }
    });
  }

  getMarqueeRect(): Rect | null {
    return this.marqueeRect;
  }

  deactivate(): void {
    if (this.editingPathLayerId) {
      this.exitPathEditMode();
    }
  }
}
